import "../style.css";
import { useStandaloneHeader, JourneyBackHeader, JourneySubpageCard } from "../shared";
import { MissionOverview } from "./MissionParts";
import useMission from "./useMission";
import "./style.css";

/**
 * Mission Details (/journey/mission — JS-03).
 *
 * `GET /ARIS/v4/journey/mission` returns the stored Mission (statement + four
 * dimensions), the current season's priorities, and the mission-alignment
 * block computed on read. Alignment degrades to null server-side — the
 * Mission itself is the point of the page, so a missing alignment hides the
 * card and never errors the screen. The Mission card, four elements and
 * evolution actions are the "Your Mission" sheet's (Figma 11268:28414,
 * MissionOverview); priorities and alignment follow below.
 *
 * Mission Evolution (v26.3): the date line reads "Created …" / "Evolved …"
 * from `display_date_kind` / `display_date` — never a version, never
 * "updated". `evolution` drives Evolve My Mission (`can_evolve`), View
 * Mission History (`has_history`), a waiting evolved Mission
 * (`pending_draft`) and an evolution left mid-conversation
 * (`open_evolution_session_id`).
 */

/** Alignment rows read the dimensions in this order. Legacy has no alignment: no Life
 * Area evidences it, so aris-ai does not score it (plan 46 #12). */
const DIMENSIONS = [
  { key: "identity" },
  { key: "relationships" },
  { key: "contribution" },
  { key: "way_of_living" },
];

/** Alignment states → badge label. Covers the aris-core `MissionAlignment`
 * enum (STRONG/GOOD/MIXED/NEEDS_ATTENTION/UNKNOWN); a state is never a
 * percentage. Unknown values fall back to a prettified raw state. */
const ALIGNMENT_LABEL = {
  STRONG: "Strong",
  GOOD: "Good",
  MIXED: "Mixed",
  NEEDS_ATTENTION: "Needs attention",
  UNKNOWN: "Early days",
};

/** "PERSONAL_GROWTH" → "Personal Growth" (life areas arrive as enum values). */
const prettify = (value) =>
  String(value || "")
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(" ");

/** Longest `season_label` still drawn as a pill beside the heading. */
const SEASON_TAG_MAX = 32;

const PrioritiesList = ({ priorities }) => {
  if (!Array.isArray(priorities) || priorities.length === 0) return null;
  const seasonLabel = priorities.find((p) => p?.season_label)?.season_label;
  // `season_label` is sometimes a short tag ("This season") and sometimes a
  // sentence describing the member's season. A tag sits beside the heading as
  // a pill; a sentence reads as a line under it — never a pill that runs off
  // the card and squeezes the heading.
  const seasonIsTag = seasonLabel && seasonLabel.length <= SEASON_TAG_MAX;
  return (
    <section>
      <div className="journey-header journey-mission__priorities-head">
        <h2 className="journey-card__title journey-mission__priorities-title">
          Priorities this season
        </h2>
        {seasonIsTag && (
          <span className="journey-badge journey-badge--muted">
            {seasonLabel}
          </span>
        )}
      </div>
      {seasonLabel && !seasonIsTag && (
        <p className="journey-mission__season">{seasonLabel}</p>
      )}
      <div className="journey-list">
        {priorities.map((priority, index) => (
          <div
            className="journey-list__row"
            key={priority.priority_id || index}
          >
            <span
              className="journey-list__tile journey-mission__order"
              style={{ background: "var(--journey-blue-bg)" }}
            >
              {index + 1}
            </span>
            <div className="journey-list__text">
              <p className="journey-list__label">{priority.text}</p>
              {Array.isArray(priority.life_areas) &&
                priority.life_areas.length > 0 && (
                  <p className="journey-list__sub">
                    {priority.life_areas
                      .map((area) => prettify(area))
                      .join(" · ")}
                  </p>
                )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const AlignmentCard = ({ alignment }) => {
  if (!alignment?.state) return null;
  const label = ALIGNMENT_LABEL[alignment.state] || prettify(alignment.state);
  const dimensionEntries = DIMENSIONS.filter(
    (dim) => alignment.dimensions?.[dim.key]
  );
  return (
    <section className="journey-card">
      <div className="journey-header" style={{ marginBottom: 16 }}>
        <h2 className="journey-card__title">Mission Alignment</h2>
        <span className="journey-badge">{label}</span>
      </div>
      {alignment.sentence && (
        <p className="journey-card__meta">{alignment.sentence}</p>
      )}
      {dimensionEntries.length > 0 && (
        <>
          <hr className="journey-divider" />
          {dimensionEntries.map((dim) => (
            <div className="journey-mission__alignment-row" key={dim.key}>
              <span className="journey-mission__alignment-label">
                {prettify(dim.key)}
              </span>
              <span className="journey-badge journey-badge--muted">
                {ALIGNMENT_LABEL[alignment.dimensions[dim.key]] ||
                  prettify(alignment.dimensions[dim.key])}
              </span>
            </div>
          ))}
        </>
      )}
    </section>
  );
};

const EmptyMission = () => (
  <section className="journey-card journey-card--status">
    <p className="journey-card__title" style={{ marginBottom: 8 }}>
      No Mission yet
    </p>
    Your Mission will appear here once you and Aris have shaped it together.
    Start a conversation whenever you're ready.
  </section>
);

function MissionScreen() {
  const { details, loading, error, actions } = useMission();

  useStandaloneHeader();

  const mission = details?.mission;

  return (
    <div className="journey-page">
      <div className="journey-page__inner">
        <JourneyBackHeader to="/journey" title="Mission" />
        <JourneySubpageCard>

          {loading && !details && (
            <div className="journey-card journey-card--status">
              Loading your mission…
            </div>
          )}
          {!loading && error && !details && (
            <div className="journey-card journey-card--status">
              We couldn't load your mission just now. Try again in a moment.
            </div>
          )}

          {!loading && !error && !mission?.statement && <EmptyMission />}

          {details && mission?.statement && (
            <>
              <MissionOverview mission={mission} evolution={details.evolution} {...actions} />
              <PrioritiesList priorities={details.priorities} />
              <AlignmentCard alignment={details.mission_alignment} />
            </>
          )}
        </JourneySubpageCard>
      </div>
    </div>
  );
}

export default MissionScreen;
